// =============================================================================
// RecruitList — /recruits — searchable, filterable list of all recruits
// S26 - pipeline enum 13->9 migration, updated PIPELINE_STATUSES + STATUS_COLORS
// S28 - card overhaul: color-bleed status pulldown, inline dispo (pre_enrollment),
//       admin note block (standard), phone display, 10/page paginator
// =============================================================================

import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/api.js';
import NotificationBell from '../components/NotificationBell.jsx';

const PIPELINE_STATUSES = [
  { value: 'new_lead',           label: 'New Lead' },
  { value: 'pre_enrollment',     label: 'Pre-Enrollment' },
  { value: 'enrolled_active',    label: 'Enrolled / Active' },
  { value: 'enrolled_paused',    label: 'Enrolled / Paused' },
  { value: 'enrolled_completed', label: 'Enrolled / Completed' },
  { value: 'dormant_lead',       label: 'Dormant Lead' },
  { value: 'dead_lead',          label: 'Dead Lead' },
  { value: 'intake_error',       label: 'Intake Error' },
  { value: 'test_account',       label: 'Test Account' },
];

const STATUS_COLORS = {
  new_lead:           'bg-gray-200 text-silver-text',
  pre_enrollment:     'bg-amber-100 text-amber-800',
  enrolled_active:    'bg-green-100 text-green-800',
  enrolled_paused:    'bg-blue-100 text-blue-800',
  enrolled_completed: 'bg-hunter-deep text-white',
  dormant_lead:       'bg-slate-200 text-slate-600',
  dead_lead:          'bg-red-200 text-red-800',
  intake_error:       'bg-orange-200 text-orange-800',
  test_account:       'bg-purple-100 text-purple-700',
};

// Border color for the color-bleed status select
const STATUS_BORDER = {
  new_lead:           'border-gray-400',
  pre_enrollment:     'border-amber-400',
  enrolled_active:    'border-green-500',
  enrolled_paused:    'border-blue-400',
  enrolled_completed: 'border-green-900',
  dormant_lead:       'border-slate-400',
  dead_lead:          'border-red-400',
  intake_error:       'border-orange-400',
  test_account:       'border-purple-400',
};

// Disposition step labels + meeting-step set (kept identical to RecruitEdit.jsx)
const STEP_LABELS = {
  intro_text:                     'Intro Text Sent',
  player_call_1:                  'Player Call 1',
  player_call_2:                  'Player Call 2',
  player_call_3:                  'Player Call 3',
  parent_call_1:                  'Parent Call 1',
  parent_call_2:                  'Parent Call 2',
  parent_call_3:                  'Parent Call 3',
  enrollment_meeting_scheduled:   'Enrollment Meeting Scheduled',
  enrollment_meeting_rescheduled: 'Enrollment Meeting Rescheduled',
  enrollment_meeting_pending:     'Enrollment Meeting Pending',
  enrollment_meeting_noshow_1:    'Enrollment Meeting No-Show 1',
  enrollment_meeting_noshow_2:    'Enrollment Meeting No-Show 2',
  enrollment_meeting_noshow_3:    'Enrollment Meeting No-Show 3',
};

const MEETING_STEPS = new Set([
  'enrollment_meeting_scheduled',
  'enrollment_meeting_rescheduled',
]);

const DISPO_STEPS = Object.keys(STEP_LABELS);

const PAGE_SIZE = 10;

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5)    return 'just now';
  if (diff < 60)   return `${diff}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return formatDate(iso);
}

// Paginator helper — returns page indices to render, skipping large gaps
function pagerRange(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);
  const set = new Set([0, totalPages - 1, page]);
  if (page > 0) set.add(page - 1);
  if (page < totalPages - 1) set.add(page + 1);
  return Array.from(set).sort((a, b) => a - b);
}

// =============================================================================
// RecruitCard — per-card state for status pulldown, dispo, and note blocks
// =============================================================================

function RecruitCard({ r, me, onRefresh, navigate }) {
  const today = new Date().toISOString().split('T')[0];

  // Status pulldown
  const [localStatus, setLocalStatus] = useState(r.status);
  const [statusSaving, setStatusSaving] = useState(false);

  // Dispo section (pre_enrollment cards)
  const [latestDispo, setLatestDispo] = useState(r.latest_disposition);
  const [cardDispoStep, setCardDispoStep] = useState(r.latest_disposition?.step || '');
  const [cardDispoNote, setCardDispoNote] = useState(r.latest_disposition?.note || '');
  const [cardDispoDate, setCardDispoDate] = useState(r.latest_disposition?.step_date || today);
  const [cardDispoSaving, setCardDispoSaving] = useState(false);
  const [cardDispoError, setCardDispoError] = useState(null);

  // Admin note block (standard cards)
  const [noteText, setNoteText] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState(null);

  async function handleStatusChange(newStatus) {
    const prev = localStatus;
    setLocalStatus(newStatus); // optimistic
    setStatusSaving(true);
    try {
      await api(`/api/recruits/${r.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: newStatus }),
      });
      onRefresh(); // refetch — drops card from view when filter active
    } catch {
      setLocalStatus(prev); // revert on error
    } finally {
      setStatusSaving(false);
    }
  }

  function handleCardDispoStepChange(newStep) {
    if (MEETING_STEPS.has(newStep)) {
      // Meeting steps → redirect to profile intake tab for full date/time capture
      navigate(`/recruits/${r.id}?tab=intake`);
      return;
    }
    setCardDispoStep(newStep);
    setCardDispoDate(today);  // reset date
    setCardDispoNote('');     // clear note
    setCardDispoError(null);
  }

  async function handleCardDispoSave() {
    if (!cardDispoStep) return;
    setCardDispoSaving(true);
    setCardDispoError(null);
    try {
      await api.put(`/api/recruits/${r.id}/dispositions/${cardDispoStep}`, {
        step_date: cardDispoDate || null,
        note: cardDispoNote || null,
      });
      setLatestDispo({ step: cardDispoStep, step_date: cardDispoDate, note: cardDispoNote, meeting_at: null });
    } catch (err) {
      setCardDispoError(err.message);
    } finally {
      setCardDispoSaving(false);
    }
  }

  async function handleAddNote() {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    setNoteError(null);
    try {
      await api.post(`/api/recruits/${r.id}/notes`, {
        note: noteText.trim(),
        author: me?.team?.full_name || undefined,
      });
      setNoteText('');
    } catch (err) {
      setNoteError(err.message);
    } finally {
      setNoteSaving(false);
    }
  }

  const colorClasses = STATUS_COLORS[localStatus] || 'bg-gray-100 text-silver-text';
  const borderClass  = STATUS_BORDER[localStatus]  || 'border-gray-300';
  const isPreEnrollment = localStatus === 'pre_enrollment';

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 flex flex-col">

      {/* Header: name + badge + color-bleed pulldown */}
      <div className="flex items-start justify-between gap-2 mb-1">
        <h2
          className="font-display font-black text-lg text-silver-text leading-tight cursor-pointer hover:text-hunter transition-colors"
          onClick={() => navigate(`/recruits/${r.id}`)}
        >
          {r.first_name} {r.last_name}
        </h2>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Read-only badge — at-a-glance color (spec §2.1: kept) */}
          <span className={`text-[0.6rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${colorClasses}`}>
            {localStatus?.replace(/_/g, ' ')}
          </span>
          {/* Color-bleed editable pulldown */}
          <select
            value={localStatus}
            onChange={e => handleStatusChange(e.target.value)}
            onClick={e => e.stopPropagation()}
            disabled={statusSaving}
            className={`text-[0.6rem] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border-2 focus:outline-none cursor-pointer transition-colors ${colorClasses} ${borderClass}`}
          >
            {PIPELINE_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Latest disposition step */}
      <p className="text-sm font-medium text-silver-text mb-1">
        {latestDispo
          ? (STEP_LABELS[latestDispo.step] || latestDispo.step)
          : <span className="text-silver-mute font-normal italic">No disposition logged</span>
        }
      </p>

      {/* Meeting callout */}
      {latestDispo?.meeting_at && MEETING_STEPS.has(latestDispo.step) && (
        <p className="text-xs text-amber-700 font-medium mb-1">
          Meeting: {formatDateTime(latestDispo.meeting_at)}
        </p>
      )}

      <hr className="border-gray-100 my-3" />

      {/* Meta row: GPA, position, class, phone */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-silver-mute mb-1">
        {r.gpa != null && <span>GPA {r.gpa}</span>}
        {r.primary_position && <span>{r.primary_position}</span>}
        {r.graduation_year && <span>Class of {r.graduation_year}</span>}
        {r.phone && <span>{r.phone}</span>}
      </div>

      <p className="text-xs text-silver-mute mb-3">Updated {relativeTime(r.updated_at)}</p>

      {/* Bottom frame — flex-1 so both card types stretch to equal height in row */}
      <div className="flex-1 flex flex-col">
        {isPreEnrollment ? (

          /* ── Pre-enrollment: inline disposition section (spec §2.3) ── */
          <div className="border border-amber-200 rounded-md p-3 bg-amber-50 flex-1 flex flex-col gap-2">
            <p className="text-[0.65rem] font-bold uppercase tracking-wide text-amber-700">Disposition</p>

            {latestDispo && (
              <p className="text-xs text-silver-mute">
                Current:{' '}
                <span className="font-medium text-silver-text">
                  {STEP_LABELS[latestDispo.step] || latestDispo.step}
                </span>
                {latestDispo.step_date && <span> — {formatDate(latestDispo.step_date)}</span>}
              </p>
            )}

            <select
              value={cardDispoStep}
              onChange={e => handleCardDispoStepChange(e.target.value)}
              className="input text-sm"
            >
              <option value="">Select step…</option>
              {DISPO_STEPS.map(s => (
                <option key={s} value={s}>
                  {STEP_LABELS[s]}{MEETING_STEPS.has(s) ? ' →' : ''}
                </option>
              ))}
            </select>

            <textarea
              value={cardDispoNote}
              onChange={e => setCardDispoNote(e.target.value)}
              placeholder="Note…"
              rows={2}
              className="input resize-none text-sm flex-1"
            />

            {cardDispoError && <p className="text-xs text-red-600">{cardDispoError}</p>}

            <button
              type="button"
              onClick={handleCardDispoSave}
              disabled={!cardDispoStep || cardDispoSaving}
              className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed w-full"
            >
              {cardDispoSaving ? 'Saving…' : 'Save'}
            </button>
          </div>

        ) : (

          /* ── Standard card: admin note block (spec §2.2) ── */
          <div className="border border-gray-200 rounded-md p-3 flex-1 flex flex-col gap-2">
            <p className="text-[0.65rem] font-bold uppercase tracking-wide text-silver-mute">Admin Note</p>

            <textarea
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Add a note…"
              rows={2}
              className="input resize-none text-sm flex-1"
            />

            {noteError && <p className="text-xs text-red-600">{noteError}</p>}

            <button
              type="button"
              onClick={handleAddNote}
              disabled={!noteText.trim() || noteSaving}
              className="btn-primary text-xs px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed w-full"
            >
              {noteSaving ? 'Saving…' : 'Add Note'}
            </button>
          </div>

        )}
      </div>
    </div>
  );
}

// =============================================================================
// RecruitList — main page component
// =============================================================================

export default function RecruitList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const [recruits, setRecruits] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => {
    const requestedPage = Number(searchParams.get('page'));
    return Number.isInteger(requestedPage) && requestedPage > 0 ? requestedPage - 1 : 0;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const debounceTimer = useRef(null);
  // Ref tracks current query values for the stable refresh callback
  const currentQueryRef = useRef({ search, statusFilter, page });

  useEffect(() => {
    currentQueryRef.current = { search, statusFilter, page };
  }, [search, statusFilter, page]);

  useEffect(() => {
    api.get('/api/auth/me').then(setMe).catch(() => {});
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  const fetchRecruits = useCallback(async (q, status, pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: PAGE_SIZE, offset: pageNum * PAGE_SIZE });
      if (q.trim()) params.set('q', q.trim());
      if (status) params.set('status', status);
      const data = await api.get(`/api/recruits?${params}`);
      setRecruits(data.recruits);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const changePage = useCallback((nextPage) => {
    setPage(nextPage);
    setSearchParams(currentParams => {
      const nextParams = new URLSearchParams(currentParams);
      if (nextPage === 0) nextParams.delete('page');
      else nextParams.set('page', String(nextPage + 1));
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  // Debounced fetch fires on any query change (search, filter, or page)
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchRecruits(search, statusFilter, page);
    }, 300);
    return () => clearTimeout(debounceTimer.current);
  }, [search, statusFilter, page, fetchRecruits]);

  // Stable refresh callback — reads current query from ref so card callbacks are stable
  const refresh = useCallback(() => {
    const { search: q, statusFilter: s, page: p } = currentQueryRef.current;
    fetchRecruits(q, s, p);
  }, [fetchRecruits]);

  const totalPages = Math.ceil(total / PAGE_SIZE) || 1;
  const startIndex = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const endIndex   = Math.min((page + 1) * PAGE_SIZE, total);

  return (
    <div className="min-h-screen bg-silver-a">

      {/* Header */}
      <header className="bg-hunter-deep border-b border-hunter">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-display font-black text-white text-2xl leading-none">CCF Front Office</div>
            <div className="text-mid-green text-[0.65rem] tracking-[0.25em] uppercase font-bold mt-1">
              Coach Cole Football
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <NotificationBell />
            {me && (
              <span className="text-silver-a">
                Welcome, <span className="font-bold text-white">{me.team.full_name}</span>
              </span>
            )}
            <Link to="/" className="text-silver-a hover:text-white underline">← Front Office</Link>
            <button onClick={signOut} className="text-silver-a hover:text-white underline">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="mb-6">
          <h1 className="font-display font-black text-2xl text-silver-text">Recruit Profiles</h1>
        </div>

        {/* Search + filter row */}
        <div className="flex gap-3 mb-5">
          <input
            type="text"
            placeholder="Search by name, email, or phone…"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              changePage(0);
            }}
            className="input flex-1"
          />
          <select
            value={statusFilter}
            onChange={e => {
              setStatusFilter(e.target.value);
              changePage(0);
            }}
            className="input w-48"
          >
            <option value="">All statuses</option>
            {PIPELINE_STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4 mb-4">
            {error}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-sm text-silver-mute py-12 text-center">Loading…</div>
        ) : recruits.length === 0 ? (
          <div className="text-sm text-silver-mute py-12 text-center">No recruits match.</div>
        ) : (
          <>
            {/* Card grid — items-stretch for equal-height rows (CSS grid default, explicit for clarity) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
              {recruits.map(r => (
                <RecruitCard
                  key={r.id}
                  r={r}
                  me={me}
                  onRefresh={refresh}
                  navigate={navigate}
                />
              ))}
            </div>

            {/* Paginator */}
            <div className="mt-6 flex flex-col items-center gap-3">
              {total > 0 && (
                <p className="text-sm text-silver-mute">
                  Showing {startIndex}–{endIndex} of {total}
                </p>
              )}
              {totalPages > 1 && (
                <div className="flex items-center gap-1 flex-wrap justify-center">
                  <button
                    onClick={() => changePage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>

                  {pagerRange(page, totalPages).reduce((acc, pageIdx, i, arr) => {
                    // Insert ellipsis between non-consecutive page numbers
                    if (i > 0 && pageIdx - arr[i - 1] > 1) {
                      acc.push(
                        <span key={`ellipsis-${pageIdx}`} className="px-2 text-silver-mute text-sm">…</span>
                      );
                    }
                    acc.push(
                      <button
                        key={pageIdx}
                        onClick={() => changePage(pageIdx)}
                        className={`px-3 py-1.5 text-sm rounded border ${
                          pageIdx === page
                            ? 'bg-hunter-deep text-white border-hunter-deep font-bold'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageIdx + 1}
                      </button>
                    );
                    return acc;
                  }, [])}

                  <button
                    onClick={() => changePage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
